/* SpawnAnything — multiplayer setup payload helpers (pure, no DOM / three.js; used by main.js and the tests).

   The host sends everything a client needs to build the identical battle once, inside the relay's `start` message
   (JSON, ≤ 1.5 MB). Numbers survive JSON bit-exactly (shortest round-trip form; only -0 becomes +0, which is why every
   client, the host included, builds the battle from the *received* payload). Typed arrays travel as base64:
   Float32 positions raw, 8-bit terrain grids run-length encoded (a 230×230 map shrinks from ~420 KB to a few KB). */

export const MP_SETUP_VERSION = 2; // 2: unit groups (cfgs/stats per group with team + slot, paintS)

/* ---------------- base64 ---------------- */
export function bytesToB64(u8) {
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
  return btoa(s);
}
export function b64ToBytes(b64) {
  const s = atob(String(b64 || '')), out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
const bytesOf = a => new Uint8Array(a.buffer, a.byteOffset, a.byteLength).slice();

/** Float32Array (or number array) -> base64 of its float32 bytes; exact. */
export const packF32 = a => bytesToB64(bytesOf(a instanceof Float32Array ? a : new Float32Array(a)));
export function unpackF32(b64) {
  const b = b64ToBytes(b64);
  return new Float32Array(b.buffer, 0, b.length >> 2);
}

/* ---------------- run-length encoding for byte grids: pairs (value, run 1..255) ---------------- */
export function rle(u8) {
  const out = new Uint8Array(u8.length * 2 + 2); let o = 0;
  for (let i = 0; i < u8.length;) {
    const v = u8[i]; let n = 1;
    while (n < 255 && i + n < u8.length && u8[i + n] === v) n++;
    out[o++] = v; out[o++] = n; i += n;
  }
  return out.slice(0, o);
}
export function unrle(p, max = 1 << 24) {
  let len = 0;
  for (let i = 1; i < p.length; i += 2) len += p[i];
  if (len > max) throw new Error('mpSetup: RLE data too large');
  const out = new Uint8Array(len); let o = 0;
  for (let i = 0; i + 1 < p.length; i += 2) { out.fill(p[i], o, o + p[i + 1]); o += p[i + 1]; }
  return out;
}

/* ---------------- typed arrays inside plain objects (terrain) ---------------- */
const TA = { Uint8Array, Int8Array, Uint16Array, Int16Array, Uint32Array, Int32Array, Float32Array, Float64Array };

/** Plain object whose values may be typed arrays -> JSON-safe object ({ $ta, rle } for every typed array). */
export function packTyped(obj) {
  if (!obj) return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (ArrayBuffer.isView(v)) {
      const name = Object.keys(TA).find(n => v instanceof TA[n]);
      if (!name) continue;
      out[k] = { $ta: name, rle: bytesToB64(rle(bytesOf(v))) };
    } else if (typeof v !== 'function') out[k] = v;
  }
  return out;
}
export function unpackTyped(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === 'object' && typeof v.$ta === 'string') {
      const C = TA[v.$ta]; if (!C) continue;
      const b = unrle(b64ToBytes(v.rle));
      out[k] = new C(b.buffer, 0, Math.floor(b.length / C.BYTES_PER_ELEMENT));
    } else out[k] = v;
  }
  return out;
}

/** Sanity check of a decoded worker terrain (a broken map must not crash the clients). */
export function validTerrain(t) {
  if (!t || !(t.n >= 8 && t.n <= 1024) || !(t.cell > 0) || !Number.isFinite(t.origin)) return false;
  const N = t.n * t.n;
  for (const k of ['level', 'water', 'ramp', 'rampDir', 'rampK', 'path', 'bridge', 'waterLevel']) if (!t[k] || t[k].length !== N) return false;
  return true;
}

/** Match-unique key for a generated recipe so it never collides with the client's own generated ones. */
export const mpKey = (tag, key) => `mp${tag}_${key}`;
